import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Sparkles, Download, Eye, CheckCircle, Image as ImageIcon,
  Users, TrendingUp, Calendar, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';

const ImpactStoryGeneratorPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectDetails, setProjectDetails] = useState(null);
  const [story, setStory] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [options, setOptions] = useState({
    includeBeforeAfter: true,
    includeTestimonials: true,
    format: 'narrative'
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetails();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${apiUrl}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchProjectDetails = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${apiUrl}/api/projects/${selectedProject}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setProjectDetails(data);
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const handleGenerate = async () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const payload = {
        projectId: selectedProject,
        includeBeforeAfter: options.includeBeforeAfter,
        includeTestimonials: options.includeTestimonials,
        format: options.format
      };

      const res = await fetch(`${apiUrl}/api/impact/story/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const result = await res.json();
        setStory(result);
        setSuccess('Impact story generated successfully!');
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to generate story');
      }
    } catch (error) {
      console.error('Error generating story:', error);
      setError('An error occurred while generating the story');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (format = 'txt') => {
    if (!story) return;

    let content = '';
    let filename = `impact-story-${selectedProject}.${format}`;

    if (format === 'txt') {
      content = story.narrative || story.story || '';
    } else if (format === 'json') {
      content = JSON.stringify(story, null, 2);
    }

    const blob = new Blob([content], {
      type: format === 'json' ? 'application/json' : 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-teal-600 hover:text-teal-700 mb-4 flex items-center"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Sparkles className="w-8 h-8 mr-3 text-teal-600" />
                Impact Story Generator
              </h1>
              <p className="text-gray-600 mt-1">Create compelling narratives from your project data</p>
            </div>
          </div>
        </div>

        {error && <AlertBanner type="error" message={error} className="mb-6" />}
        {success && <AlertBanner type="success" message={success} className="mb-6" />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Project Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Project</h2>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">-- Select a Project --</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>

              {projectDetails && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center text-sm">
                    <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                    <span className="text-gray-700">{projectDetails.geography}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Users className="w-4 h-4 mr-2 text-gray-500" />
                    <span className="text-gray-700">{projectDetails.expectedBeneficiaries} beneficiaries</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                    <span className="text-gray-700">{projectDetails.duration} months</span>
                  </div>
                </div>
              )}
            </div>

            {/* Story Options */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Story Options</h2>
              
              <div className="space-y-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={options.includeBeforeAfter}
                    onChange={(e) => setOptions({ ...options, includeBeforeAfter: e.target.checked })}
                    className="mt-1 mr-3 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Before & After</span>
                    <p className="text-xs text-gray-500">Include comparative analysis</p>
                  </div>
                </label>

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={options.includeTestimonials}
                    onChange={(e) => setOptions({ ...options, includeTestimonials: e.target.checked })}
                    className="mt-1 mr-3 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">Testimonials</span>
                    <p className="text-xs text-gray-500">Include beneficiary quotes</p>
                  </div>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Format
                  </label>
                  <select
                    value={options.format}
                    onChange={(e) => setOptions({ ...options, format: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="narrative">Narrative Story</option>
                    <option value="report">Formal Report</option>
                    <option value="presentation">Presentation Slides</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <Button
              onClick={handleGenerate}
              disabled={loading || !selectedProject}
              className="w-full text-white"
              style={{ background: '#14B8A6' }}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Story
                </>
              )}
            </Button>

            {story && (
              <div className="space-y-2">
                <Button
                  onClick={() => handleDownload('txt')}
                  variant="outline"
                  className="w-full text-teal-600 border-teal-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download as Text
                </Button>
                <Button
                  onClick={() => handleDownload('json')}
                  variant="outline"
                  className="w-full text-gray-600 border-gray-300"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download as JSON
                </Button>
              </div>
            )}
          </div>

          {/* Story Preview */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-8 min-h-[600px]">
              {!story ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-20">
                  <FileText className="w-20 h-20 text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    No Story Generated Yet
                  </h3>
                  <p className="text-gray-500 max-w-md">
                    Select a project and click "Generate Story" to create a compelling impact narrative
                  </p>
                </div>
              ) : (
                <div className="prose prose-teal max-w-none">
                  {/* Story Header */}
                  {story.title && (
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                      {story.title}
                    </h1>
                  )}
                  
                  {story.subtitle && (
                    <p className="text-lg text-gray-600 mb-6 italic">
                      {story.subtitle}
                    </p>
                  )}

                  {/* Story Content */}
                  {story.narrative && (
                    <div className="text-gray-700 leading-relaxed space-y-4">
                      {story.narrative.split('\n\n').map((paragraph, idx) => (
                        <p key={idx}>{paragraph}</p>
                      ))}
                    </div>
                  )}

                  {story.story && typeof story.story === 'string' && (
                    <div className="text-gray-700 leading-relaxed space-y-4">
                      {story.story.split('\n\n').map((paragraph, idx) => (
                        <p key={idx}>{paragraph}</p>
                      ))}
                    </div>
                  )}

                  {/* Key Highlights */}
                  {story.highlights && Array.isArray(story.highlights) && story.highlights.length > 0 && (
                    <div className="mt-8 p-6 bg-teal-50 rounded-lg">
                      <h3 className="text-xl font-semibold text-teal-900 mb-4">Key Highlights</h3>
                      <ul className="space-y-2">
                        {story.highlights.map((highlight, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Impact Metrics */}
                  {story.metrics && (
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                      {story.metrics.beneficiaries && (
                        <div className="p-4 bg-blue-50 rounded-lg text-center">
                          <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-blue-900">{story.metrics.beneficiaries}</p>
                          <p className="text-sm text-blue-700">Beneficiaries</p>
                        </div>
                      )}
                      {story.metrics.budget && (
                        <div className="p-4 bg-green-50 rounded-lg text-center">
                          <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-green-900">₹{story.metrics.budget.toLocaleString('en-IN')}</p>
                          <p className="text-sm text-green-700">Budget Allocated</p>
                        </div>
                      )}
                      {story.metrics.duration && (
                        <div className="p-4 bg-purple-50 rounded-lg text-center">
                          <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-purple-900">{story.metrics.duration}</p>
                          <p className="text-sm text-purple-700">Months</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Testimonials */}
                  {story.testimonials && Array.isArray(story.testimonials) && story.testimonials.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Voices from the Ground</h3>
                      <div className="space-y-4">
                        {story.testimonials.map((testimonial, idx) => (
                          <div key={idx} className="p-4 bg-gray-50 rounded-lg border-l-4 border-teal-500">
                            <p className="text-gray-700 italic mb-2">"{testimonial.quote}"</p>
                            <p className="text-sm text-gray-600">— {testimonial.author}, {testimonial.role}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Before & After */}
                  {story.beforeAfter && (
                    <div className="mt-8">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Transformation</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-4 bg-red-50 rounded-lg">
                          <h4 className="font-semibold text-red-900 mb-2">Before</h4>
                          <p className="text-gray-700 text-sm">{story.beforeAfter.before}</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg">
                          <h4 className="font-semibold text-green-900 mb-2">After</h4>
                          <p className="text-gray-700 text-sm">{story.beforeAfter.after}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Conclusion */}
                  {story.conclusion && (
                    <div className="mt-8 p-6 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Looking Ahead</h3>
                      <p className="text-gray-700">{story.conclusion}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpactStoryGeneratorPage;
